"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Plus, X } from "lucide-react";
import { ChallengeGrid } from "@/components/structure/ChallengeGrid";
import { getDriftCanvases } from "@/app/(app)/(linear)/app/session/canvas/actions";

type HistoryMode = "sessions" | "canvases";

export type HistoryDrawerUser = {
  id?: string;
  challenges?: unknown[];
};

type CanvasSummary = {
  id: string;
  challenge: string | null;
  updatedAt: string | Date;
  _count?: {
    ideas?: number;
  };
};

type HistoryDrawerProps = {
  user?: HistoryDrawerUser | null;
  showHistory?: boolean;
  setShowHistory?: (showHistory: boolean) => void;
};

const historyModes: Array<{ id: HistoryMode; label: string }> = [
  { id: "sessions", label: "Sessions" },
  { id: "canvases", label: "Canvases" },
];

const formatCanvasDate = (date: string | Date) =>
  new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

export function HistoryDrawer({
  user,
  showHistory,
  setShowHistory,
}: HistoryDrawerProps) {
  const pathname = usePathname();
  const [mode, setMode] = useState<HistoryMode>("sessions");
  const [canvases, setCanvases] = useState<CanvasSummary[]>([]);
  const [loadingCanvases, setLoadingCanvases] = useState(false);

  useEffect(() => {
    if (!showHistory || mode !== "canvases" || !user?.id) return;

    let cancelled = false;
    setLoadingCanvases(true);

    getDriftCanvases(user.id)
      .then((result) => {
        if (!cancelled) {
          setCanvases(result as CanvasSummary[]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCanvases(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mode, showHistory, user?.id]);

  return (
    <nav
      className={`w-[440px] max-w-[calc(100vw-24px)] left-0 fixed h-screen z-[52] top-0 transition-all duration-300 ease-in-out ${
        showHistory ? "translate-x-0" : "translate-x-[-100%]"
      }`}
      aria-label="History"
    >
      <div
        className="h-full overflow-auto border-r border-black/10 bg-white text-gray-900 shadow-[18px_0_60px_rgba(15,23,42,0.12)]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15, 23, 42, 0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(15, 23, 42, 0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      >
        <header className="sticky top-0 z-[11] border-b border-black/10 bg-white/95 px-6 pt-8 pb-5 backdrop-blur">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#F50078]">
                Thinknado
              </p>
              <h3 className="text-2xl font-semibold leading-none tracking-normal text-gray-950">
                History
              </h3>
            </div>
            <button
              className="flex h-9 w-9 items-center justify-center border border-black/15 text-gray-500 transition hover:border-[#F50078] hover:text-[#F50078]"
              onClick={() => setShowHistory?.(false)}
            >
              <span className="sr-only">Close history</span>
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-400">
              Revisit past sessions and canvases.
            </p>
            <button
              onClick={() => {
                if (mode === "canvases") {
                  location.href = "/app/session/canvas/new";
                  return;
                }

                if (pathname === "/app") {
                  location.reload();
                  return;
                }

                location.href = "/app";
              }}
              className="flex shrink-0 items-center gap-1.5 border border-black/20 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-700 transition hover:border-[#F50078] hover:text-[#F50078]"
            >
              New <Plus className="h-3.5 w-3.5 text-[#F50078]" />
            </button>
          </div>
          <div
            className="mt-5 grid grid-cols-2 border border-black/15 bg-white p-1"
            role="group"
            aria-label="History type"
          >
            {historyModes.map((option) => {
              const selected = mode === option.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setMode(option.id)}
                  className={`px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] transition ${
                    selected
                      ? "bg-gray-950 text-white"
                      : "text-gray-500 hover:bg-gray-50 hover:text-gray-950"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </header>

        <div className="px-5 py-6 pb-32">
          {mode === "sessions" ? (
            <ChallengeGrid challenges={user?.challenges || []} />
          ) : (
            <CanvasHistoryList
              canvases={canvases}
              loading={loadingCanvases}
              hasUser={Boolean(user?.id)}
            />
          )}
        </div>
      </div>
    </nav>
  );
}

function CanvasHistoryList({
  canvases,
  loading,
  hasUser,
}: {
  canvases: CanvasSummary[];
  loading: boolean;
  hasUser: boolean;
}) {
  if (!hasUser) {
    return (
      <div className="border border-black/10 bg-white px-5 py-8 text-center text-sm text-gray-500">
        Sign in to see canvases.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="border border-black/10 bg-white px-5 py-8 text-center text-sm text-gray-400">
        Loading canvases...
      </div>
    );
  }

  if (canvases.length === 0) {
    return (
      <div className="border border-black/10 bg-white px-5 py-8 text-center">
        <p className="text-sm text-gray-500">No canvases yet.</p>
        <Link
          href="/app/session/canvas/new"
          className="mt-2 inline-flex text-sm font-medium text-[#F50078] hover:underline"
        >
          Create one
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {canvases.map((canvas) => {
        const ideaCount = canvas._count?.ideas || 0;

        return (
          <Link
            key={canvas.id}
            href={`/app/session/canvas/${canvas.id}`}
            className="block border border-black/10 bg-white px-5 py-4 transition hover:border-[#F50078] hover:shadow-[0_12px_32px_rgba(15,23,42,0.08)]"
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <h4 className="text-sm font-semibold leading-snug text-gray-950">
                {canvas.challenge || "Untitled Canvas"}
              </h4>
              <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-gray-400">
                {formatCanvasDate(canvas.updatedAt)}
              </span>
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">
              {ideaCount} idea{ideaCount !== 1 ? "s" : ""}
            </p>
          </Link>
        );
      })}
    </div>
  );
}
